import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, ArrowUp, FlaskConical, Plus, Trash2 } from 'lucide-react';

import type { AuthMeResponse } from '@/api/auth.api';
import {
  createRoutingRule,
  deleteRoutingRule,
  dryRunRoutingRules,
  listCsGroups,
  listRoutingRules,
  reorderRoutingRules,
  updateRoutingRule,
  RULE_ACTION_TYPES,
  RULE_MATCHER_FIELDS,
  type CsRoutingRule,
  type DryRunResult,
  type RuleActionType,
  type RuleMatcherField,
} from '@/api/cs/routing.api';
import { CS_TICKET_PRIORITIES, CS_TIERS } from '@/api/cs/tickets.api';
import { CsErrorCard, CsInlineError, CsLoading, SELECT_CLS, TEXTAREA_CLS } from '@/components/cs/PageStates';
import { describeCsError } from '@/components/cs/csError';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/cn';
import { hasCapability } from '@/lib/capabilities';
import { useToast } from '@/hooks/useToast';

/**
 * Operations → Routing (spec §3.2 + §3.10). Rules list ordered by
 * position (up/down reorder → full-list POST /reorder contract),
 * enable/disable, editor dialog with a form mode for common cases and
 * a raw-JSON mode (client validates JSON shape; the server's Zod DSL
 * is the real gate and its issues render inline), plus a dry-run that
 * shows per-rule match counts over recent tickets.
 *
 * Note: seeded rules can legitimately be inactive (e.g. `brand_is`
 * rules with no V1 data source) — inactive is data, not an error.
 */

const NO_VALUE_FIELDS: RuleMatcherField[] = ['assignee_empty', 'group_empty'];

// ── Form-mode model ─────────────────────────────────────────────────────

interface FormMatcher {
  field: RuleMatcherField;
  valuesText: string;
}

interface FormAction {
  type: RuleActionType;
  /** Interpreted per type: groupSlug | email-or-membershipId | CSV tags | priority | tier | spam tag. */
  value: string;
}

interface EditorForm {
  match: 'all' | 'any';
  matchers: FormMatcher[];
  actions: FormAction[];
}

function formToConditions(form: EditorForm): unknown {
  return {
    match: form.match,
    matchers: form.matchers.map((m) => {
      if (NO_VALUE_FIELDS.includes(m.field)) return { field: m.field };
      const parts = m.valuesText
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v !== '');
      if (m.field === 'inbox_is') return { field: m.field, values: parts.map((v) => Number(v)) };
      return { field: m.field, values: parts };
    }),
  };
}

function formToActions(form: EditorForm): unknown {
  return form.actions.map((a) => {
    switch (a.type) {
      case 'assign_group':
        return { type: a.type, groupSlug: a.value.trim() };
      case 'assign_membership':
        return a.value.includes('@')
          ? { type: a.type, membershipEmail: a.value.trim() }
          : { type: a.type, membershipId: a.value.trim() };
      case 'add_tags':
        return {
          type: a.type,
          tags: a.value
            .split(',')
            .map((t) => t.trim())
            .filter((t) => t !== ''),
        };
      case 'set_priority':
        return { type: a.type, priority: a.value };
      case 'set_tier':
        return { type: a.type, tier: a.value };
      case 'spam_close':
        return a.value.trim() === '' ? { type: a.type } : { type: a.type, tag: a.value.trim() };
      default:
        return { type: a.type };
    }
  });
}

/**
 * Best-effort JSON → form; null when the rule needs raw-JSON mode.
 * Inputs are treated as untrusted runtime JSON (the Prisma Json column
 * carries whatever was stored), so every access is guarded.
 */
function jsonToForm(conditionsJson: unknown, actionsJson: unknown): EditorForm | null {
  if (typeof conditionsJson !== 'object' || conditionsJson === null || Array.isArray(conditionsJson)) {
    return null;
  }
  const conditions = conditionsJson as { match?: unknown; matchers?: unknown; orgExcludeGroup?: unknown };
  if (conditions.orgExcludeGroup !== undefined) return null; // leakage metadata — JSON-only
  if (conditions.match !== 'all' && conditions.match !== 'any') return null;
  if (!Array.isArray(conditions.matchers)) return null;
  const matcherList: unknown[] = conditions.matchers;

  const matchers: FormMatcher[] = [];
  for (const raw of matcherList) {
    if (typeof raw !== 'object' || raw === null) return null;
    const m = raw as { field?: unknown; values?: unknown };
    if (typeof m.field !== 'string' || !(RULE_MATCHER_FIELDS as readonly string[]).includes(m.field)) {
      return null;
    }
    const values: unknown[] = Array.isArray(m.values) ? m.values : [];
    matchers.push({ field: m.field as RuleMatcherField, valuesText: values.map(String).join(', ') });
  }

  if (!Array.isArray(actionsJson)) return null;
  const actionList: unknown[] = actionsJson;
  const actions: FormAction[] = [];
  for (const raw of actionList) {
    if (typeof raw !== 'object' || raw === null) return null;
    const a = raw as {
      type?: unknown;
      groupSlug?: unknown;
      membershipEmail?: unknown;
      membershipId?: unknown;
      tags?: unknown;
      priority?: unknown;
      tier?: unknown;
      tag?: unknown;
    };
    switch (a.type) {
      case 'assign_group':
        actions.push({ type: a.type, value: typeof a.groupSlug === 'string' ? a.groupSlug : '' });
        break;
      case 'assign_membership':
        actions.push({
          type: a.type,
          value:
            typeof a.membershipEmail === 'string'
              ? a.membershipEmail
              : typeof a.membershipId === 'string'
                ? a.membershipId
                : '',
        });
        break;
      case 'add_tags':
        actions.push({
          type: a.type,
          value: Array.isArray(a.tags) ? (a.tags as unknown[]).map(String).join(', ') : '',
        });
        break;
      case 'set_priority':
        actions.push({ type: a.type, value: typeof a.priority === 'string' ? a.priority : '' });
        break;
      case 'set_tier':
        actions.push({ type: a.type, value: typeof a.tier === 'string' ? a.tier : '' });
        break;
      case 'spam_close':
        actions.push({ type: a.type, value: typeof a.tag === 'string' ? a.tag : '' });
        break;
      case 'auto_resolve':
      case 'stop_processing':
        actions.push({ type: a.type, value: '' });
        break;
      default:
        return null;
    }
  }
  return { match: conditions.match, matchers, actions };
}

// ── Tab ─────────────────────────────────────────────────────────────────

export function RoutingTab({ me }: { me: AuthMeResponse }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const canManage = hasCapability(me, 'manage_cs_routing');

  const rulesQuery = useQuery({ queryKey: ['cs', 'routing', 'rules'], queryFn: listRoutingRules });
  const groupsQuery = useQuery({ queryKey: ['cs', 'groups'], queryFn: listCsGroups, enabled: canManage });

  const [editing, setEditing] = useState<CsRoutingRule | 'new' | null>(null);
  const [deleting, setDeleting] = useState<CsRoutingRule | null>(null);
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['cs', 'routing', 'rules'] });
  }

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => updateRoutingRule(id, { active }),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeCsError(err, 'Could not toggle the rule.'), { variant: 'danger' });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (ruleIds: string[]) => reorderRoutingRules(ruleIds),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeCsError(err, 'Could not reorder the rules.'), { variant: 'danger' });
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteRoutingRule(id),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
      toast.show('Rule deleted.', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeCsError(err, 'Could not delete the rule.'), { variant: 'danger' });
    },
  });

  const dryRunMutation = useMutation({
    mutationFn: () => dryRunRoutingRules(),
    onSuccess: setDryRun,
    onError: (err) => {
      toast.show(describeCsError(err, 'Dry run failed.'), { variant: 'danger' });
    },
  });

  if (rulesQuery.isLoading) return <CsLoading />;
  if (rulesQuery.error) {
    return (
      <CsErrorCard title="Couldn't load routing rules" error={rulesQuery.error} fallback="Unknown error" />
    );
  }

  const rules = rulesQuery.data ?? [];
  const dryRunByRule = new Map((dryRun?.rules ?? []).map((r) => [r.ruleId, r]));

  function move(index: number, delta: -1 | 1): void {
    const target = index + delta;
    if (target < 0 || target >= rules.length) return;
    const ids = rules.map((r) => r.id);
    const a = ids[index];
    const b = ids[target];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[target] = a;
    reorderMutation.mutate(ids);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-kw-muted">
          Rules fire top-down (position ascending) on new tickets.
          {dryRun ? ` Dry run evaluated ${String(dryRun.ticketsEvaluated)} recent ticket(s).` : ''}
        </p>
        <div className="flex gap-2">
          {canManage ? (
            <Button
              size="sm"
              variant="secondary"
              isLoading={dryRunMutation.isPending}
              onClick={() => {
                dryRunMutation.mutate();
              }}
            >
              <FlaskConical className="size-4" />
              Dry run
            </Button>
          ) : null}
          {canManage ? (
            <Button
              size="sm"
              onClick={() => {
                setEditing('new');
              }}
            >
              <Plus className="size-4" />
              New rule
            </Button>
          ) : null}
        </div>
      </div>

      <Card className="p-0">
        {rules.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13px] text-kw-muted">No routing rules yet.</p>
        ) : (
          <ul className="divide-y divide-kw-border">
            {rules.map((rule, index) => {
              const dr = dryRunByRule.get(rule.id);
              return (
                <li key={rule.id} className="flex items-start gap-3 px-4 py-3">
                  {canManage ? (
                    <div className="flex shrink-0 flex-col">
                      <button
                        type="button"
                        aria-label={`Move ${rule.name} up`}
                        disabled={index === 0 || reorderMutation.isPending}
                        onClick={() => {
                          move(index, -1);
                        }}
                        className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark disabled:opacity-30"
                      >
                        <ArrowUp className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        aria-label={`Move ${rule.name} down`}
                        disabled={index === rules.length - 1 || reorderMutation.isPending}
                        onClick={() => {
                          move(index, 1);
                        }}
                        className="rounded p-0.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark disabled:opacity-30"
                      >
                        <ArrowDown className="size-3.5" />
                      </button>
                    </div>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[12px] tabular-nums text-kw-ghost">#{rule.position}</span>
                      <p
                        className={cn(
                          'text-[14px] font-medium',
                          rule.active ? 'text-kw-shark' : 'text-kw-muted',
                        )}
                      >
                        {rule.name}
                      </p>
                      {rule.isSystem ? <Badge variant="info">System</Badge> : null}
                      {!rule.active ? <Badge>Disabled</Badge> : null}
                      {rule.stopAfterMatch ? <Badge variant="warning">Stops</Badge> : null}
                      {dr ? (
                        dr.invalidConditions ? (
                          <Badge variant="danger">Invalid conditions</Badge>
                        ) : (
                          <Badge variant={dr.matchCount > 0 ? 'success' : 'neutral'}>
                            {String(dr.matchCount)} match(es)
                          </Badge>
                        )
                      ) : null}
                    </div>
                    <p className="mt-0.5 truncate text-[12px] text-kw-muted">{summarizeRule(rule)}</p>
                  </div>
                  {canManage ? (
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={toggleMutation.isPending}
                        onClick={() => {
                          toggleMutation.mutate({ id: rule.id, active: !rule.active });
                        }}
                      >
                        {rule.active ? 'Disable' : 'Enable'}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setEditing(rule);
                        }}
                      >
                        Edit
                      </Button>
                      <button
                        type="button"
                        aria-label={`Delete ${rule.name}`}
                        onClick={() => {
                          setDeleting(rule);
                        }}
                        className="rounded p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {editing !== null ? (
        <RuleEditorModal
          rule={editing === 'new' ? null : editing}
          groupSlugs={(groupsQuery.data ?? []).map((g) => g.slug)}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={() => {
            setEditing(null);
            invalidate();
            toast.show('Rule saved.', { variant: 'success' });
          }}
        />
      ) : null}

      <Modal
        open={deleting !== null}
        onClose={() => {
          setDeleting(null);
        }}
        title="Delete routing rule"
        size="sm"
      >
        {deleting ? (
          <div className="space-y-4">
            <p className="text-[13px] text-kw-shark">
              Delete <strong>{deleting.name}</strong>?
              {deleting.isSystem
                ? ' This is a seeded system rule — deleting it removes a routing guarantee.'
                : ''}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setDeleting(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                isLoading={deleteMutation.isPending}
                onClick={() => {
                  deleteMutation.mutate(deleting.id);
                }}
              >
                Delete rule
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function summarizeRule(rule: CsRoutingRule): string {
  // Json columns are runtime data — widen before inspecting.
  const conditions = rule.conditionsJson as { match?: unknown; matchers?: unknown } | null;
  const matcherCount = conditions && Array.isArray(conditions.matchers) ? conditions.matchers.length : 0;
  const actions: unknown = rule.actionsJson;
  const actionSummary = Array.isArray(actions)
    ? actions
        .map((a) => {
          if (typeof a !== 'object' || a === null) return '?';
          const type = (a as { type?: unknown }).type;
          return typeof type === 'string' ? type : '?';
        })
        .join(', ')
    : 'invalid actions';
  const match = conditions && typeof conditions.match === 'string' ? conditions.match : '?';
  return `${String(matcherCount)} condition(s), match ${match} → ${actionSummary}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Rule editor — form mode + raw JSON mode
// ─────────────────────────────────────────────────────────────────────────

const DEFAULT_FORM: EditorForm = {
  match: 'all',
  matchers: [{ field: 'subject_contains', valuesText: '' }],
  actions: [{ type: 'assign_group', value: '' }],
};

function RuleEditorModal({
  rule,
  groupSlugs,
  onClose,
  onSaved,
}: {
  rule: CsRoutingRule | null;
  groupSlugs: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const initialForm = rule ? jsonToForm(rule.conditionsJson, rule.actionsJson) : DEFAULT_FORM;

  const [name, setName] = useState(rule?.name ?? '');
  const [stopAfterMatch, setStopAfterMatch] = useState(rule?.stopAfterMatch ?? false);
  const [mode, setMode] = useState<'form' | 'json'>(initialForm ? 'form' : 'json');
  const [form, setForm] = useState<EditorForm>(initialForm ?? DEFAULT_FORM);
  const [conditionsText, setConditionsText] = useState(
    JSON.stringify(rule?.conditionsJson ?? formToConditions(DEFAULT_FORM), null, 2),
  );
  const [actionsText, setActionsText] = useState(
    JSON.stringify(rule?.actionsJson ?? formToActions(DEFAULT_FORM), null, 2),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: (input: { conditions: unknown; actions: unknown }) =>
      rule
        ? updateRoutingRule(rule.id, { name: name.trim(), stopAfterMatch, ...input })
        : createRoutingRule({ name: name.trim(), stopAfterMatch, ...input }),
    onSuccess: onSaved,
  });

  /** Client-side JSON validation — parse + basic shape before the server's Zod. */
  function parseJsonMode(): { conditions: unknown; actions: unknown } | null {
    let conditions: unknown;
    let actions: unknown;
    try {
      conditions = JSON.parse(conditionsText);
    } catch (err) {
      setJsonError(`Conditions is not valid JSON: ${err instanceof Error ? err.message : 'parse error'}`);
      return null;
    }
    try {
      actions = JSON.parse(actionsText);
    } catch (err) {
      setJsonError(`Actions is not valid JSON: ${err instanceof Error ? err.message : 'parse error'}`);
      return null;
    }
    if (typeof conditions !== 'object' || conditions === null || Array.isArray(conditions)) {
      setJsonError('Conditions must be a JSON object like {"match":"all","matchers":[…]}.');
      return null;
    }
    if (!Array.isArray(actions) || actions.length === 0) {
      setJsonError('Actions must be a non-empty JSON array of action objects.');
      return null;
    }
    setJsonError(null);
    return { conditions, actions };
  }

  function switchMode(next: 'form' | 'json'): void {
    if (next === mode) return;
    if (next === 'json') {
      setConditionsText(JSON.stringify(formToConditions(form), null, 2));
      setActionsText(JSON.stringify(formToActions(form), null, 2));
      setJsonError(null);
      setMode('json');
      return;
    }
    // JSON → form: only when the JSON round-trips into the simple form model.
    const parsed = parseJsonMode();
    if (!parsed) return;
    const asForm = jsonToForm(parsed.conditions, parsed.actions);
    if (!asForm) {
      setJsonError('This rule uses features the form editor cannot represent — keep editing as JSON.');
      return;
    }
    setForm(asForm);
    setMode('form');
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!name.trim()) return;
    const payload =
      mode === 'json'
        ? parseJsonMode()
        : { conditions: formToConditions(form), actions: formToActions(form) };
    if (!payload) return;
    saveMutation.mutate(payload);
  }

  return (
    <Modal open onClose={onClose} title={rule ? `Edit rule: ${rule.name}` : 'New routing rule'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {rule?.isSystem ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-warning-text/30 bg-kw-warning-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-warning-text" />
            <p className="text-[12px] text-kw-warning-text">
              This is a seeded SYSTEM rule (e.g. the Biogenetix hard route). Edits are audited and can change
              routing guarantees — proceed deliberately.
            </p>
          </div>
        ) : null}

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-64 flex-1">
            <Input
              label="Rule name"
              name="cs-rule-name"
              required
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-[13px] text-kw-shark">
            <input
              type="checkbox"
              checked={stopAfterMatch}
              onChange={(e) => {
                setStopAfterMatch(e.target.checked);
              }}
            />
            Stop after match
          </label>
          <div className="flex rounded-kw-md border border-kw-border p-0.5">
            <button
              type="button"
              aria-pressed={mode === 'form'}
              onClick={() => {
                switchMode('form');
              }}
              className={cn(
                'rounded-kw-md px-3 py-1 text-[13px] font-medium',
                mode === 'form' ? 'bg-kw-cerulean text-white' : 'text-kw-muted hover:text-kw-shark',
              )}
            >
              Form
            </button>
            <button
              type="button"
              aria-pressed={mode === 'json'}
              onClick={() => {
                switchMode('json');
              }}
              className={cn(
                'rounded-kw-md px-3 py-1 text-[13px] font-medium',
                mode === 'json' ? 'bg-kw-cerulean text-white' : 'text-kw-muted hover:text-kw-shark',
              )}
            >
              JSON
            </button>
          </div>
        </div>

        {mode === 'form' ? (
          <FormEditor form={form} setForm={setForm} groupSlugs={groupSlugs} />
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-[13px] font-medium text-kw-label">Conditions JSON</span>
              <textarea
                aria-label="Conditions JSON"
                value={conditionsText}
                onChange={(e) => {
                  setConditionsText(e.target.value);
                }}
                rows={12}
                spellCheck={false}
                className={cn(TEXTAREA_CLS, 'font-mono text-[12px]')}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[13px] font-medium text-kw-label">Actions JSON</span>
              <textarea
                aria-label="Actions JSON"
                value={actionsText}
                onChange={(e) => {
                  setActionsText(e.target.value);
                }}
                rows={12}
                spellCheck={false}
                className={cn(TEXTAREA_CLS, 'font-mono text-[12px]')}
              />
            </label>
          </div>
        )}

        {jsonError ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">{jsonError}</p>
          </div>
        ) : null}
        <CsInlineError error={saveMutation.error} fallback="Could not save the rule." />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={saveMutation.isPending} disabled={!name.trim()}>
            {rule ? 'Save rule' : 'Create rule'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Form-mode sub-editor
// ─────────────────────────────────────────────────────────────────────────

const VALUE_HINTS: Partial<Record<RuleActionType, string>> = {
  assign_group: 'group slug',
  assign_membership: 'email or membership id',
  add_tags: 'comma-separated tags',
  spam_close: 'tag (default is_spam)',
};

function FormEditor({
  form,
  setForm,
  groupSlugs,
}: {
  form: EditorForm;
  setForm: (updater: (prev: EditorForm) => EditorForm) => void;
  groupSlugs: string[];
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Conditions</span>
          <select
            aria-label="Match mode"
            value={form.match}
            onChange={(e) => {
              const match = e.target.value as 'all' | 'any';
              setForm((prev) => ({ ...prev, match }));
            }}
            className={SELECT_CLS}
          >
            <option value="all">Match ALL</option>
            <option value="any">Match ANY</option>
          </select>
        </div>
        {form.matchers.map((m, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <select
              aria-label={`Matcher ${String(i + 1)} field`}
              value={m.field}
              onChange={(e) => {
                const field = e.target.value as RuleMatcherField;
                setForm((prev) => ({
                  ...prev,
                  matchers: prev.matchers.map((x, xi) => (xi === i ? { ...x, field } : x)),
                }));
              }}
              className={SELECT_CLS}
            >
              {RULE_MATCHER_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            {!NO_VALUE_FIELDS.includes(m.field) ? (
              <input
                aria-label={`Matcher ${String(i + 1)} values`}
                value={m.valuesText}
                onChange={(e) => {
                  const valuesText = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    matchers: prev.matchers.map((x, xi) => (xi === i ? { ...x, valuesText } : x)),
                  }));
                }}
                placeholder={m.field === 'inbox_is' ? 'comma-separated inbox ids' : 'comma-separated values'}
                className="min-w-56 flex-1 rounded-kw-md border border-kw-border bg-white px-3 py-1.5 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
              />
            ) : (
              <span className="text-[12px] text-kw-muted">(no values needed)</span>
            )}
            <button
              type="button"
              aria-label={`Remove matcher ${String(i + 1)}`}
              disabled={form.matchers.length <= 1}
              onClick={() => {
                setForm((prev) => ({ ...prev, matchers: prev.matchers.filter((_, xi) => xi !== i) }));
              }}
              className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text disabled:opacity-30"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            setForm((prev) => ({
              ...prev,
              matchers: [...prev.matchers, { field: 'subject_contains', valuesText: '' }],
            }));
          }}
        >
          <Plus className="size-4" />
          Add condition
        </Button>
      </div>

      <div className="space-y-2">
        <span className="text-[12px] font-medium uppercase tracking-wide text-kw-muted">Actions</span>
        {form.actions.map((a, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <select
              aria-label={`Action ${String(i + 1)} type`}
              value={a.type}
              onChange={(e) => {
                const type = e.target.value as RuleActionType;
                setForm((prev) => ({
                  ...prev,
                  actions: prev.actions.map((x, xi) => (xi === i ? { type, value: '' } : x)),
                }));
              }}
              className={SELECT_CLS}
            >
              {RULE_ACTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            {a.type === 'assign_group' ? (
              <select
                aria-label={`Action ${String(i + 1)} group`}
                value={a.value}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    actions: prev.actions.map((x, xi) => (xi === i ? { ...x, value } : x)),
                  }));
                }}
                className={SELECT_CLS}
              >
                <option value="">Choose group…</option>
                {groupSlugs.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : a.type === 'set_priority' ? (
              <select
                aria-label={`Action ${String(i + 1)} priority`}
                value={a.value}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    actions: prev.actions.map((x, xi) => (xi === i ? { ...x, value } : x)),
                  }));
                }}
                className={SELECT_CLS}
              >
                <option value="">Choose priority…</option>
                {CS_TICKET_PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            ) : a.type === 'set_tier' ? (
              <select
                aria-label={`Action ${String(i + 1)} tier`}
                value={a.value}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    actions: prev.actions.map((x, xi) => (xi === i ? { ...x, value } : x)),
                  }));
                }}
                className={SELECT_CLS}
              >
                <option value="">Choose tier…</option>
                {CS_TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            ) : a.type === 'auto_resolve' || a.type === 'stop_processing' ? (
              <span className="text-[12px] text-kw-muted">(no parameters)</span>
            ) : (
              <input
                aria-label={`Action ${String(i + 1)} value`}
                value={a.value}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    actions: prev.actions.map((x, xi) => (xi === i ? { ...x, value } : x)),
                  }));
                }}
                placeholder={VALUE_HINTS[a.type] ?? 'value'}
                className="min-w-56 flex-1 rounded-kw-md border border-kw-border bg-white px-3 py-1.5 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
              />
            )}
            <button
              type="button"
              aria-label={`Remove action ${String(i + 1)}`}
              disabled={form.actions.length <= 1}
              onClick={() => {
                setForm((prev) => ({ ...prev, actions: prev.actions.filter((_, xi) => xi !== i) }));
              }}
              className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text disabled:opacity-30"
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        ))}
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            setForm((prev) => ({ ...prev, actions: [...prev.actions, { type: 'add_tags', value: '' }] }));
          }}
        >
          <Plus className="size-4" />
          Add action
        </Button>
      </div>
    </div>
  );
}
